import type { NavigationItem } from '../types/navigation.types';
import { computed, readonly, ref } from 'vue';

export interface UseNavigationTreeOptions {
  allowMultipleSelection?: boolean;
  showCheckboxes?: boolean;
  showIcons?: boolean;
  maxDepth?: number;
  autoExpandFirstLevel?: boolean;
  externalSelectedItemId?: string | null;
  externalExpandedItems?: Set<string>;
  useGlobalState?: boolean;
}

const globalSelectedItemId = ref<string | null>(null);
const globalExpandedItems = ref<Set<string>>(new Set());

export function useNavigationTree(options: UseNavigationTreeOptions = {}) {
  const {
    allowMultipleSelection = false,
    showCheckboxes = true,
    showIcons = true,
    maxDepth = Infinity,
    autoExpandFirstLevel = false,
    externalSelectedItemId = null,
    externalExpandedItems = new Set(),
    useGlobalState = false
  } = options;

  const selectedItemId = useGlobalState ? globalSelectedItemId : ref<string | null>(externalSelectedItemId);
  const expandedItems = useGlobalState ? globalExpandedItems : ref<Set<string>>(new Set(externalExpandedItems));
  const items = ref<NavigationItem[]>([]);

  if (!useGlobalState) {
    if (externalSelectedItemId) {
      selectedItemId.value = externalSelectedItemId;
    }

    if (externalExpandedItems.size > 0) {
      externalExpandedItems.forEach(id => expandedItems.value.add(id));
    }
  }

  const selectedItem = computed(() => {
    if (!selectedItemId.value) return null;
    
    const findItem = (items: NavigationItem[]): NavigationItem | null => {
      for (const item of items) {
        if (item.id === selectedItemId.value) return item;
        if (item.children?.length) {
          const found = findItem(item.children);
          if (found) return found;
        }
      }
      return null;
    };
    
    return findItem(items.value);
  });

  const isExpanded = (itemId: string) => expandedItems.value.has(itemId);
  const isSelected = (itemId: string) => selectedItemId.value === itemId;
  const hasChildren = (item: NavigationItem) => item.children && item.children.length > 0;
  const canExpand = (item: NavigationItem, level: number) => hasChildren(item) && level < maxDepth;
  const isLeaf = (item: NavigationItem) => !hasChildren(item);

  const collapseDescendants = (item: NavigationItem) => {
    if (!item.children?.length) return;
    
    for (const child of item.children) {
      expandedItems.value.delete(child.id);
      collapseDescendants(child);
    }
  };

  const collapseSiblings = (currentItemId: string) => {
    for (const sibling of items.value) {
      if (sibling.id !== currentItemId) {
        expandedItems.value.delete(sibling.id);
        collapseDescendants(sibling);
      }
    }
  };

  const collapseOtherRoots = (currentItemId: string) => {
    for (const root of items.value) {
      if (root.id !== currentItemId) {
        expandedItems.value.delete(root.id);
        collapseDescendants(root);
      }
    }
  };

  const toggleExpand = (item: NavigationItem) => {
    const currentlyExpanded = expandedItems.value.has(item.id);
    
    if (currentlyExpanded) {
      expandedItems.value.delete(item.id);
      collapseDescendants(item);
      return;
    }
    
    expandedItems.value.add(item.id);
  };

  const handleItemClick = (item: NavigationItem) => {
    selectedItemId.value = item.id;
  };

  const handleItemExpand = (item: NavigationItem) => {
    toggleExpand(item);
  };

  const handleItemCollapse = (item: NavigationItem) => {
    expandedItems.value.delete(item.id);
    collapseDescendants(item);
  };

  const handleItemToggle = (item: NavigationItem) => {
    toggleExpand(item);
  };

  const handleItemClickAndExpand = (item: NavigationItem, level: number) => {
    handleItemClick(item);
    
    if (isLeaf(item)) return;

    const wasExpanded = isExpanded(item.id);
    if (!wasExpanded) {
      if (level === 0) {
        collapseOtherRoots(item.id);
      } else {
        collapseSiblings(item.id);
      }
    }
    
    toggleExpand(item);
  };

  const initializeItems = (newItems: NavigationItem[]) => {
    items.value = newItems;
    
    if (autoExpandFirstLevel) {
      newItems.forEach(item => {
        if (hasChildren(item)) {
          expandedItems.value.add(item.id);
        }
      });
    }
  };

  const clearSelection = () => {
    selectedItemId.value = null;
  };

  const clearExpanded = () => {
    expandedItems.value.clear();
  };

  const reset = () => {
    clearSelection();
    clearExpanded();
  };

  const setSelectedItem = (itemId: string | null) => {
    selectedItemId.value = itemId;
  };

  const getSelectedItem = () => {
    return selectedItemId.value;
  };

  const addExpandedItem = (itemId: string) => {
    expandedItems.value.add(itemId);
  };

  const removeExpandedItem = (itemId: string) => {
    expandedItems.value.delete(itemId);
  };

  return {
    expandedItems: readonly(expandedItems),
    selectedItemId: readonly(selectedItemId),
    items: readonly(items),
    selectedItem,
    isExpanded,
    isSelected,
    hasChildren,
    canExpand,
    isLeaf,
    handleItemClick,
    handleItemExpand,
    handleItemCollapse,
    handleItemToggle,
    handleItemClickAndExpand,
    initializeItems,
    clearSelection,
    clearExpanded,
    reset,
    setSelectedItem,
    getSelectedItem,
    addExpandedItem,
    removeExpandedItem,
    isGlobalState: useGlobalState
  };
} 